<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import FileUploadField from '../components/FileUploadField.vue'
import ColorPickerModal from '../components/ColorPickerModal.vue'
import ProductPickerModal from '../components/ProductPickerModal.vue'
import PersonnelPickerModal from '../components/PersonnelPickerModal.vue'
import ThreadCodePickerModal from '../components/ThreadCodePickerModal.vue'
import EquipmentPickerModal from '../components/EquipmentPickerModal.vue'
import SockSmartDimensions from '../components/SockSmartDimensions.vue'
import SockTypeImageGrid from '../components/SockTypeImageGrid.vue'
import { emptyFixedMeasurements, fixedMeasurementsFromDimensions, normalizeFixedMeasurements } from '../sockMeasurements'
import type { FixedMeasurements } from '../sockMeasurements'
import { defaultSockElements, defaultSockYarns } from '../sockPassportStructure'
import { passportSockTypeImage } from '../sockTypeImages'
import type { SockTypeImageOption } from '../sockTypeImages'
import { restoreDecorativeElement, serializeDecorativeElement } from '../sockDecorations'
import { applyBomToPassport, legacyStageCodeForOperation, mergeBomMaterialsIntoPassport, applyPassportMaterialsToBom, stageCodeForOperation } from '../bomPassportLink'
import { resolveAssetUrl } from '../productPresentation'
import { uzbekTechnicalPassportSectionNames } from '../technicalPassportSectionNames'
import { technicalPassportApprovalPayload } from '../technicalPassportApproval'
import {
  createTechnicalPassport,
  getProduct,
  getTechnicalPassport,
  getBomVersion,
  listEquipmentReferences,
  listColorReferences,
  listThreadCodes,
  listPersonnelEmployees,
  listProductBomVersions,
  updateBomVersion,
  updateTechnicalPassport,
} from '../productService'
import type {
  BomVersion,
  BomVersionSummary,
  EquipmentReference,
  ColorReference,
  EngineeringProduct,
  ProductionEmployee,
  ThreadCodeReference,
  TechnicalPassport,
  TechnicalPassportPayload,
  TechnicalPassportStatus,
  TechnicalPassportMaterial,
  TechnicalPassportMachine,
  TechnicalPassportMeasurement,
  TechnicalPassportPackaging,
  TechnicalPassportQcItem,
  TechnicalPassportLabel,
  TechnicalPassportThread,
  TechnicalPassportCosting,
  TechnicalPassportAttachment,
  TechnicalPassportRoute,
  TechnicalPassportDimensions,
  TechnicalPassportElement,
  TechnicalPassportYarn,
} from '../types'

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function mergeFixedMeasurements(primary: FixedMeasurements, fallback: FixedMeasurements): FixedMeasurements {
  return Object.fromEntries(
    Object.entries(primary).map(([key, value]) => [key, value ?? fallback[key as keyof FixedMeasurements]]),
  ) as FixedMeasurements
}

const route = useRoute()
const router = useRouter()
const client = useApiClient()
const auth = useAuthStore()
const { t, te } = useI18n()
const passportId = computed(() => Number(route.params.id))
const isEditing = computed(() => Number.isInteger(passportId.value) && passportId.value > 0)
const productId = computed(() => Number(route.query.product_id))
const product = ref<EngineeringProduct | null>(null)
const passport = ref<TechnicalPassport | null>(null)
const activeBom = ref<BomVersion | null>(null)
/** Newest BOM version of the product — the source of the raw-material stage options. */
const latestBom = ref<BomVersion | null>(null)
/** Writing the shared materials back to the BOM needs `bom.manage`. */
const canManageBom = computed(() => (auth.user?.permissions ?? []).includes('bom.manage'))
const equipmentReferences = ref<EquipmentReference[]>([])
const approvalPeople = ref<ProductionEmployee[]>([])
const colorReferences = ref<ColorReference[]>([])
const threadCodeReferences = ref<ThreadCodeReference[]>([])
const referenceCatalogs = reactive({
  equipment: false,
  personnel: false,
  colors: false,
  threadCodes: false,
})
const pendingMainProductImageBase64 = ref('')
const pendingMainProductImagePreview = ref('')
const pendingTechnicalDrawingBase64 = ref('')
const pendingTechnicalDrawingPreview = ref('')
const mainProductImageStatus = ref('')
const technicalDrawingStatus = ref('')
const knittingProgramStatus = ref('')
const loading = ref(false)
const saving = ref(false)
const loadError = ref('')
const submitError = ref('')
const fieldErrors = reactive<Record<string, string>>({})
const initialFormState = ref<Record<string, unknown> | null>(null)
type PassportTab = 'general' | 'structure' | 'materials' | 'machines' | 'threads' | 'labels' | 'packaging' | 'attachments'
const tabs = computed<Array<{ id: PassportTab; label: string; prefixes: string[]; fields: string[] }>>(() => [
  { id: 'general', label: t('engineering.passportForm.general'), prefixes: ['product', 'sock_'], fields: ['sock_type', 'size_range', 'sock_weight_gram', 'machine_type', 'machine_needle_count', 'description'] },
  { id: 'structure', label: t('engineering.passportForm.measurements'), prefixes: ['fixed_measurement_', 'measurement_'], fields: ['fixed_measurements', 'measurements'] },
  { id: 'materials', label: t('engineering.passportForm.rawMaterials'), prefixes: ['material_'], fields: ['raw_materials'] },
  { id: 'threads', label: t('engineering.passportForm.threads'), prefixes: ['thread_'], fields: ['threads'] },
  { id: 'machines', label: t('engineering.passportForm.machines'), prefixes: ['machine_'], fields: ['machines'] },
  { id: 'packaging', label: t('engineering.passportForm.packaging'), prefixes: ['packaging_'], fields: ['packaging'] },
  { id: 'labels', label: t('engineering.passportForm.labels'), prefixes: ['label_'], fields: ['labels'] },
  { id: 'attachments', label: t('engineering.passportForm.attachments'), prefixes: ['legacy_main_product_image', 'legacy_technical_drawing_image', 'legacy_knitting_machine_design_files'], fields: ['attachments', 'main_product_image', 'technical_drawing_image'] },
])
function tabHasErrors(tab: (typeof tabs.value)[number]): boolean {
  return Object.keys(fieldErrors).some((key) => tab.prefixes.some((prefix) => key.startsWith(prefix)))
}
function tabErrorCount(tab: (typeof tabs.value)[number]): number {
  return Object.keys(fieldErrors).filter((key) => tab.prefixes.some((prefix) => key.startsWith(prefix))).length
}
function scrollToSection(id: PassportTab): void {
  document.getElementById(`passport-panel-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
function tabHasChanges(tab: (typeof tabs.value)[number]): boolean {
  if (!initialFormState.value) return false
  return tab.fields.some((field) =>
    JSON.stringify((form as Record<string, unknown>)[field])
    !== JSON.stringify(initialFormState.value?.[field]),
  )
}
const fieldErrorCount = computed(() => Object.keys(fieldErrors).length)
const changedSectionCount = computed(() => tabs.value.filter(tabHasChanges).length)
const missingSectionNames = computed(() =>
  uzbekTechnicalPassportSectionNames(passport.value?.completeness?.missing_sections),
)
const sockTypeLabel = computed(() => {
  if (form.sock_type === 'uzun') return t('engineering.passportForm.long')
  if (form.sock_type === 'orta') return t('engineering.passportForm.middle')
  if (form.sock_type === 'kalta') return t('engineering.passportForm.short')
  if (form.sock_type === 'sledi') return t('engineering.passportForm.noShow')
  return ''
})

const sockTypeImageOptions = computed<SockTypeImageOption[]>(() => [
  { value: 'uzun', label: t('engineering.passportForm.long'), image: passportSockTypeImage('uzun') },
  { value: 'orta', label: t('engineering.passportForm.middle'), image: passportSockTypeImage('orta') },
  { value: 'kalta', label: t('engineering.passportForm.short'), image: passportSockTypeImage('kalta') },
  { value: 'sledi', label: t('engineering.passportForm.noShow'), image: passportSockTypeImage('sledi') },
])

const fieldTabMap: Partial<Record<string, PassportTab>> = {
  rubber_return_place: 'general',
  cotton_yarn: 'general',
  spandex_yarn: 'general',
  rubber_yarn: 'general',
  nylon_yarn: 'general',
  main_product_image: 'attachments',
  technical_drawing_image: 'attachments',
  knitting_machine_design_files: 'attachments',
}

function passportFieldLabel(field: string): string {
  const labelKey = {
    rubber_return_place: 'engineering.passportForm.rubberReturnPlace',
    cotton_yarn: 'engineering.passportForm.cottonYarn',
    spandex_yarn: 'engineering.passportForm.spandexYarn',
    rubber_yarn: 'engineering.passportForm.rubberYarn',
    nylon_yarn: 'engineering.passportForm.nylonYarn',
    main_product_image: 'engineering.passportForm.mainProductImage',
    technical_drawing_image: 'engineering.passportForm.technicalDrawingImage',
    knitting_machine_design_files: 'engineering.passportForm.knittingMachineDesignFiles',
    technologist_id: 'engineering.passportDetail.technologist',
    shift_manager_id: 'engineering.passportDetail.shiftManager',
    manager_id: 'engineering.passportDetail.manager',
  } as const
  const key = labelKey[field as keyof typeof labelKey]
  return key ? t(key) : field
}

function passportTabLabel(tab: PassportTab): string {
  return tabs.value.find((item) => item.id === tab)?.label ?? tab
}

const approvalRecommendations = computed<string[]>(() => {
  const recommendations: string[] = []
  if (!form.technologist_id) recommendations.push(t('engineering.passportForm.approvalFixTechnologist'))
  if (!form.shift_manager_id) recommendations.push(t('engineering.passportForm.approvalFixShiftManager'))
  if (!form.manager_id) recommendations.push(t('engineering.passportForm.approvalFixManager'))
  if (fieldErrors.machine_0) recommendations.push(t('engineering.passportForm.approvalFixMachine'))
  if (fieldErrors.change_description) recommendations.push(t('engineering.passportForm.approvalFixRevision'))
  if (!recommendations.length && submitError.value) recommendations.push(t('engineering.passportForm.approvalRetry'))
  return recommendations
})

const approvalErrorTitle = computed(() =>
  submitError.value
    ? t('engineering.passportForm.approvalApiError')
    : Object.keys(fieldErrors).length
      ? t('engineering.passportForm.approvalValidationError')
      : '',
)
function resolvedAttachmentUrl(value?: string | null): string {
  return resolveAssetUrl(value ?? null) ?? ''
}
const productPicker = reactive({
  open: false,
  target: '' as 'material' | 'packaging' | 'label' | '',
  index: -1,
})
const personnelPicker = reactive({
  open: false,
  target: '' as 'technologist' | 'shift_manager' | 'manager' | '',
})
const threadColorPicker = reactive({
  open: false,
  index: -1,
  target: 'thread' as 'thread' | 'material',
})
const threadCodePicker = reactive({
  open: false,
  index: -1,
  target: 'thread' as 'thread' | 'material',
})
const machinePicker = reactive({ open: false, index: -1 })
const initialDimensions: TechnicalPassportDimensions = {
  foot_length_mm: 250,
  leg_height_mm: 150,
  foot_width_mm: 90,
  cuff_height_mm: 35,
  cuff_width_mm: 80,
  toe_length_mm: 45,
  heel_length_mm: 60,
  instep_length_mm: 80,
}
const form = reactive({
  template_type: '',
  sock_type: '' as '' | 'uzun' | 'orta' | 'kalta' | 'sledi',
  season: '',
  size_range: '',
  sock_weight_gram: 66,
  machine_type: '',
  machine_needle_count: 0,
  description: '',
  prepared_by: '',
  rubber_return_place: '',
  cotton_yarn: '',
  spandex_yarn: '',
  rubber_yarn: '',
  nylon_yarn: '',
  main_product_image: '',
  technical_drawing_image: '',
  technologist_id: undefined as number | undefined,
  shift_manager_id: undefined as number | undefined,
  manager_id: undefined as number | undefined,
  technologist_approved_at: '',
  shift_manager_approved_at: '',
  manager_approved_at: '',
  status: 'draft' as TechnicalPassportStatus,
  reject_reason: '',
  revision_no: '1',
  revision_date: new Date().toISOString().slice(0, 10),
  changed_by: '',
  change_description: '',
  raw_materials: [] as TechnicalPassportMaterial[],
  routes: [] as TechnicalPassportRoute[],
  measurements: [] as TechnicalPassportMeasurement[],
  machines: [] as TechnicalPassportMachine[],
  qc_items: [] as TechnicalPassportQcItem[],
  packaging: [] as TechnicalPassportPackaging[],
  labels: [] as TechnicalPassportLabel[],
  threads: [] as TechnicalPassportThread[],
  dimensions: { ...initialDimensions } as TechnicalPassportDimensions,
  fixed_measurements: emptyFixedMeasurements() as FixedMeasurements,
  elements: defaultSockElements(initialDimensions) as TechnicalPassportElement[],
  yarns: defaultSockYarns() as TechnicalPassportYarn[],
  costing: [] as TechnicalPassportCosting[],
  attachments: [] as TechnicalPassportAttachment[],
})

const targetProductId = computed(() =>
  isEditing.value ? passport.value?.product_id ?? 0 : productId.value,
)
const bomStageOptions = computed(() => {
  const options: Array<{ value: string; label: string }> = []
  const seen = new Set<string>()
  // The stage list is the BOM of THIS product (newest version), one option per operation.
  const operations = latestBom.value?.operations ?? activeBom.value?.operations ?? []
  for (const operation of operations) {
    const value = stageCodeForOperation(operation)
    if (seen.has(value)) continue
    seen.add(value)
    options.push({
      value,
      label: operation.operation_sequence
        ? `${operation.operation_sequence}. ${operation.operation_name}`
        : operation.operation_name,
    })
  }
  if (options.length) return options
  return ['knitting', 'rosso', 'boarding', 'labeling', 'packaging', 'finishing']
    .map((stage) => ({ value: stage, label: t(`engineering.manufacturingStage.${stage}`) }))
})
const machineTypeOptions = computed(() => {
  const options: string[] = []
  const seen = new Set<string>()
  for (const equipment of equipmentReferences.value) {
    const value = equipment.machine_type?.trim()
    const key = value?.toLocaleLowerCase()
    if (!value || !key || seen.has(key)) continue
    seen.add(key)
    options.push(value)
  }
  const current = form.machine_type.trim()
  if (current && !seen.has(current.toLocaleLowerCase())) options.push(current)
  return options.sort((left, right) => machineTypeLabel(left).localeCompare(machineTypeLabel(right)))
})
function machineTypeLabel(value: string): string {
  const key = `engineering.equipment.machineTypeOptions.${value}`
  return te(key) ? t(key) : value
}
const backPath = computed(() =>
  targetProductId.value ? `/products/${targetProductId.value}` : '/products',
)
const productDisplayName = computed(() => {
  const name = product.value?.name?.trim()
  const sku = product.value?.sku?.trim()
  if (name && sku) return `${name} · ${sku}`
  return name || sku || ''
})
const currentUserName = computed(() => auth.user?.name?.trim() || '')

function applyCurrentUserDefaults(): void {
  if (currentUserName.value) {
    form.changed_by = currentUserName.value
  }
  if (currentUserName.value && !form.prepared_by.trim()) {
    form.prepared_by = currentUserName.value
  }
}

watch(currentUserName, () => {
  applyCurrentUserDefaults()
}, { immediate: true })

function validate(): boolean {
  Object.keys(fieldErrors).forEach((key) => delete fieldErrors[key])
  if (!targetProductId.value) fieldErrors.product = t('engineering.passportValidation.productRequired')

  const requiresApproval = form.status === 'pending_approval' || form.status === 'approved'
  if (requiresApproval) {
    if (!form.sock_type) fieldErrors.sock_type = t('engineering.passportValidation.sockTypeRequired')
    if (!Number.isFinite(Number(form.sock_weight_gram)) || Number(form.sock_weight_gram) <= 0) {
      fieldErrors.sock_weight_gram = t('engineering.passportValidation.sockWeight')
    }
    if (!Number.isFinite(Number(form.machine_needle_count)) || Number(form.machine_needle_count) <= 0) {
      fieldErrors.machine_needle_count = t('engineering.passportValidation.needles')
    }
    if (!form.machines.length) fieldErrors.machine_0 = t('engineering.passportValidation.machineRequired')
  }

  Object.entries(form.fixed_measurements).forEach(([key, value]) => {
    if (value !== null && value !== undefined && Number(value) < 0) {
      fieldErrors[`fixed_measurement_${key}`] = t('engineering.passportValidation.dimensions')
    }
  })
  form.raw_materials.forEach((row, index) => {
    if (!row.material_product_id || Number(row.material_product_id) <= 0) {
      fieldErrors[`material_${index}`] = t('engineering.passportValidation.materialId')
    } else if (!row.consumption_per_pair || Number(row.consumption_per_pair) <= 0) {
      fieldErrors[`material_${index}`] = t('engineering.passportValidation.consumption')
    }
  })
  form.machines.forEach((row, index) => {
    if ((!row.equipment_reference_id || row.equipment_reference_id <= 0) && !row.machine_name?.trim()) {
      fieldErrors[`machine_${index}`] = t('engineering.passportValidation.equipment')
    } else if (!row.needle_count || row.needle_count <= 0) {
      fieldErrors[`machine_${index}`] = t('engineering.passportValidation.needles')
    }
  })
  form.packaging.forEach((row, index) => {
    if (
      !row.packaging_product_id &&
      !row.packaging_reference_id &&
      !row.packaging_name?.trim()
    ) {
      fieldErrors[`packaging_${index}`] = t('engineering.passportValidation.packaging')
    } else if (!row.quantity || row.quantity <= 0) {
      fieldErrors[`packaging_${index}`] = t('engineering.passportValidation.quantity')
    }
  })
  form.labels.forEach((row, index) => {
    if (!row.label_name?.trim()) fieldErrors[`label_${index}`] = t('engineering.passportValidation.label')
  })
  form.threads.forEach((row, index) => {
    if (!row.thread_color_name?.trim() || !row.thread_code?.trim()) {
      fieldErrors[`thread_${index}`] = t('engineering.passportValidation.thread')
    }
  })
  const firstInvalidTab = tabs.value.find(tabHasErrors)
  if (firstInvalidTab) scrollToSection(firstInvalidTab.id)
  return Object.keys(fieldErrors).length === 0
}

function openProductPicker(target: 'material' | 'packaging' | 'label', index: number): void {
  productPicker.target = target
  productPicker.index = index
  productPicker.open = true
}

function openPersonnelPicker(target: 'technologist' | 'shift_manager' | 'manager'): void {
  personnelPicker.target = target
  personnelPicker.open = true
  void ensureApprovalPeople()
}

function selectedPersonnelName(id?: number): string {
  if (!id) return ''
  const employee = approvalPeople.value.find((person) => Number(person.user_id ?? person.id) === id)
  return employee ? `${employee.first_name} ${employee.last_name}`.trim() : ''
}

function selectApprovalPerson(employee: ProductionEmployee): void {
  const approverId = employee.user_id ?? employee.id
  if (personnelPicker.target === 'technologist') {
    form.technologist_id = approverId
  } else if (personnelPicker.target === 'shift_manager') {
    form.shift_manager_id = approverId
  } else if (personnelPicker.target === 'manager') {
    form.manager_id = approverId
  }
  personnelPicker.open = false
}

function selectProduct(product: EngineeringProduct): void {
  if (productPicker.target === 'material') {
    const row = form.raw_materials[productPicker.index]
    if (row) {
      row.material_product_id = product.id
      row.material_name_snapshot = product.name
      row.uom = product.unit || row.uom
    }
  } else if (productPicker.target === 'packaging') {
    const row = form.packaging[productPicker.index]
    if (row) {
      row.packaging_product_id = product.id
      row.packaging_reference_id = undefined
      row.packaging_name = product.name
    }
  } else if (productPicker.target === 'label') {
    const row = form.labels[productPicker.index]
    if (row) {
      row.label_name = product.name
      row.label_code = product.sku
    }
  }
  productPicker.open = false
}

function addMaterial(): void {
  form.raw_materials.push({
    stage_code: 'knitting',
    material_product_id: undefined,
    material_name_snapshot: '',
    consumption_per_pair: 0,
    waste_percent: 0,
    uom: 'kg',
    color_name: '',
    thread_code: '',
    is_required_for_start: true,
    sort_order: form.raw_materials.length + 1,
  })
}
function removeMaterial(index: number): void { form.raw_materials.splice(index, 1) }

function removeMachine(index: number): void {
  form.machines.splice(index, 1)
  renumberMachines()
}
function renumberMachines(): void {
  form.machines.forEach((row, index) => { row.sort_order = index + 1 })
}
async function openMachinePicker(index = -1): Promise<void> {
  machinePicker.index = index
  // Reuse the catalog loaded for the machine-type selector.
  await ensureEquipmentReferences()
  machinePicker.open = true
}
function machineRowFromEquipment(equipment: EquipmentReference): TechnicalPassportMachine {
  return {
    equipment_reference_id: equipment.id,
    machine_name: equipment.name,
    needle_count: equipment.base_amount && equipment.base_amount > 0 ? equipment.base_amount : undefined,
    is_compatible: true,
    sort_order: 1,
  }
}
function selectMachine(equipment: EquipmentReference): void {
  machinePicker.open = false
  const target = machinePicker.index
  machinePicker.index = -1
  const next = machineRowFromEquipment(equipment)
  if (!form.machine_type && equipment.machine_type?.trim()) {
    form.machine_type = equipment.machine_type.trim()
  }
  const replaced = form.machines[target]
  if (target >= 0 && replaced) {
    form.machines.splice(target, 1, {
      ...next,
      id: replaced.id,
      needle_count: Number(replaced.needle_count) > 0 ? replaced.needle_count : next.needle_count,
      is_compatible: replaced.is_compatible,
      sort_order: replaced.sort_order,
    })
  } else if (!form.machines.some((row) => Number(row.equipment_reference_id) === equipment.id)) {
    form.machines.push(next)
  }
  // One row per catalog machine: drop any duplicate that a row change created.
  const unique: TechnicalPassportMachine[] = []
  form.machines.forEach((row) => {
    const equipmentId = Number(row.equipment_reference_id ?? 0)
    if (equipmentId > 0 && unique.some((entry) => Number(entry.equipment_reference_id) === equipmentId)) return
    unique.push(row)
  })
  form.machines.splice(0, form.machines.length, ...unique)
  renumberMachines()
}

function addQcItem(): void {
  form.qc_items.push({ stage: 'knitting', item_name: '', acceptable_value: '', inspection_method: '', notes: '', is_required: true, sort_order: form.qc_items.length + 1 })
}
function removeQcItem(index: number): void { form.qc_items.splice(index, 1) }

function addPackaging(): void {
  form.packaging.push({ packaging_product_id: undefined, packaging_reference_id: undefined, packaging_name: '', quantity: 1, sort_order: form.packaging.length + 1 })
}
function removePackaging(index: number): void { form.packaging.splice(index, 1) }

const packagingTotal = computed(() =>
  form.packaging.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0),
)

function addLabel(): void {
  form.labels.push({ label_name: '', label_code: '', is_required: true, sort_order: form.labels.length + 1 })
}
function removeLabel(index: number): void { form.labels.splice(index, 1) }

function addThread(): void {
  form.threads.push({ thread_color_name: '', thread_code: '', thread_code_id: undefined, thread_code_master: null, sort_order: form.threads.length + 1 })
}
function removeThread(index: number): void { form.threads.splice(index, 1) }
function openThreadColorPicker(index: number, target: 'thread' | 'material' = 'thread'): void {
  threadColorPicker.index = index
  threadColorPicker.target = target
  threadColorPicker.open = true
  void ensureColorReferences()
}
function selectThreadColor(color: ColorReference): void {
  if (threadColorPicker.target === 'material') {
    const row = form.raw_materials[threadColorPicker.index]
    if (row) {
      row.color_name = color.name
      row.thread_code = color.code || row.thread_code
    }
  } else {
    const row = form.threads[threadColorPicker.index]
    if (row) {
      row.thread_color_name = color.name
      row.thread_code = color.code || row.thread_code
    }
  }
  threadColorPicker.open = false
}
function openThreadCodePicker(index: number, target: 'thread' | 'material' = 'thread'): void {
  threadCodePicker.index = index
  threadCodePicker.target = target
  threadCodePicker.open = true
  void ensureThreadCodeReferences()
}
function selectThreadCode(threadCode: ThreadCodeReference): void {
  if (threadCodePicker.target === 'material') {
    const row = form.raw_materials[threadCodePicker.index]
    if (row) {
      row.thread_code = threadCode.code
    }
  } else {
    const row = form.threads[threadCodePicker.index]
    if (row) {
      row.thread_code_id = threadCode.id
      row.thread_code = threadCode.code
      row.thread_code_master = threadCode
    }
  }
  threadCodePicker.open = false
}
function addThreadCodeReference(threadCode: ThreadCodeReference): void {
  if (!threadCodeReferences.value.some((item) => item.id === threadCode.id)) {
    threadCodeReferences.value = [threadCode, ...threadCodeReferences.value]
  }
}

function addAttachment(): void {
  form.attachments.push({ attachment_type: 'other', file_name: '', file_url: '', mime_type: '', notes: '', sort_order: form.attachments.length + 1 })
}
function removeAttachment(index: number): void { form.attachments.splice(index, 1) }
function machineDesignFileUrls(): string[] {
  return form.attachments
    .filter((row) => row.attachment_type === 'machine_file')
    .map((row) => row.file_url?.trim() || '')
    .filter(Boolean)
}

/** The single `machine_file` attachment row backing the knitting program file field. */
function machineFileRow(): TechnicalPassportAttachment | undefined {
  return form.attachments.find((row) => row.attachment_type === 'machine_file')
}
function knittingProgramFile(): string {
  return machineDesignFileUrls()[0] ?? ''
}
function setKnittingProgramFile(value: string): void {
  const existing = machineFileRow()
  if (!value.trim()) {
    if (existing) {
      existing.file_url = ''
      existing.file_name = ''
    }
    return
  }
  if (existing) {
    existing.file_url = value
    if (!existing.file_name?.trim()) existing.file_name = value.split('/').pop() || value
    return
  }
  form.attachments.push({
    attachment_type: 'machine_file',
    file_name: value.split('/').pop() || value,
    file_url: value,
    mime_type: '',
    notes: '',
    sort_order: form.attachments.length + 1,
  })
}
function applyKnittingProgramFile(file: File): void {
  const existing = machineFileRow()
  if (existing) {
    existing.file_name = file.name
    existing.mime_type = file.type || existing.mime_type
    existing.file_size = file.size
  } else {
    form.attachments.push({
      attachment_type: 'machine_file',
      file_name: file.name,
      file_url: '',
      mime_type: file.type || '',
      file_size: file.size,
      notes: '',
      sort_order: form.attachments.length + 1,
    })
  }
  knittingProgramStatus.value = t('engineering.passportForm.attachmentSelectedStatus', { size: file.size })
}
function applyAttachmentFile(file: File, row: TechnicalPassportAttachment): void {
  row.file_name = file.name
  row.mime_type = file.type || row.mime_type
  row.file_size = file.size
  if (!row.file_url?.trim()) {
    row.file_url = ''
  }
}
function isSupportedInlineImage(file: File): boolean {
  return ['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result)
        return
      }
      reject(new Error(t('engineering.passportForm.fileReadError')))
    }
    reader.onerror = () => reject(new Error(t('engineering.passportForm.fileReadError')))
    reader.readAsDataURL(file)
  })
}

async function applyMainProductImageFile(file: File): Promise<void> {
  if (!isSupportedInlineImage(file)) {
    submitError.value = t('engineering.passportForm.imageUploadTypeError')
    mainProductImageStatus.value = ''
    return
  }
  try {
    mainProductImageStatus.value = t('engineering.passportForm.imageQueued')
    pendingMainProductImageBase64.value = await readFileAsDataUrl(file)
    pendingMainProductImagePreview.value = pendingMainProductImageBase64.value
    form.main_product_image = file.name
    submitError.value = ''
    mainProductImageStatus.value = t('engineering.passportForm.imageQueuedSuccess')
  } catch (error: unknown) {
    mainProductImageStatus.value = ''
    submitError.value = formatApiError(error)
  }
}
async function applyTechnicalDrawingFile(file: File): Promise<void> {
  if (!isSupportedInlineImage(file)) {
    submitError.value = t('engineering.passportForm.imageUploadTypeError')
    technicalDrawingStatus.value = ''
    return
  }
  try {
    technicalDrawingStatus.value = t('engineering.passportForm.imageQueued')
    pendingTechnicalDrawingBase64.value = await readFileAsDataUrl(file)
    pendingTechnicalDrawingPreview.value = pendingTechnicalDrawingBase64.value
    form.technical_drawing_image = file.name
    submitError.value = ''
    technicalDrawingStatus.value = t('engineering.passportForm.imageQueuedSuccess')
  } catch (error: unknown) {
    technicalDrawingStatus.value = ''
    submitError.value = formatApiError(error)
  }
}

function payload(): TechnicalPassportPayload {
  const primaryMachine = form.machines.find((machine) => machine.is_compatible !== false)
    ?? form.machines[0]
  return {
    product_id: targetProductId.value,
    sock_type: form.sock_type,
    size_range: form.size_range.trim() || undefined,
    sock_weight_gram: Number(form.sock_weight_gram),
    machine_type: form.machine_type.trim() || undefined,
    machine_needle_count: Number(form.machine_needle_count) > 0
      ? Number(form.machine_needle_count)
      : Number(primaryMachine?.needle_count ?? 0),
    fixed_measurements: Object.fromEntries(
      Object.entries(form.fixed_measurements).map(([key, value]) => [
        key,
        value === null || value === undefined ? null : Number(value),
      ]),
    ),
    main_product_image: form.main_product_image.trim() || undefined,
    main_product_image_base64: pendingMainProductImageBase64.value || undefined,
    technical_drawing_image: form.technical_drawing_image.trim() || undefined,
    technical_drawing_image_base64: pendingTechnicalDrawingBase64.value || undefined,
    knitting_machine_design_files: machineDesignFileUrls(),
    description: form.description.trim() || undefined,
    prepared_by: form.prepared_by.trim() || form.changed_by.trim() || undefined,
    ...technicalPassportApprovalPayload(form),
    reject_reason: form.reject_reason.trim() || undefined,
    status: form.status,
    raw_materials: form.raw_materials.map((row, index) => ({
      ...row,
      material_product_id: Number(row.material_product_id),
      consumption_per_pair: Number(row.consumption_per_pair),
      waste_percent: Number(row.waste_percent ?? 0),
      sort_order: index + 1,
    })),
    measurements: form.measurements.map((row, index) => ({
      ...row,
      row_count: row.row_count ? Number(row.row_count) : undefined,
      sort_order: index + 1,
    })),
    machines: form.machines.map((row, index) => ({
      ...row,
      equipment_reference_id: Number(row.equipment_reference_id),
      needle_count: Number(row.needle_count),
      sort_order: index + 1,
    })),
    packaging: form.packaging.map((row, index) => ({
      ...row,
      packaging_product_id: row.packaging_product_id ? Number(row.packaging_product_id) : undefined,
      packaging_reference_id: row.packaging_reference_id ? Number(row.packaging_reference_id) : undefined,
      quantity: Number(row.quantity),
      sort_order: index + 1,
    })),
    labels: form.labels.map((row, index) => ({ ...row, sort_order: index + 1 })),
    threads: form.threads.map((row, index) => ({
      ...row,
      thread_code_id: row.thread_code_id ? Number(row.thread_code_id) : undefined,
      sort_order: index + 1,
    })),
    elements: form.elements.map((row, index) => ({
      ...serializeDecorativeElement(row),
      length_mm: row.length_mm === null || row.length_mm === undefined ? null : Number(row.length_mm),
      width_mm: row.width_mm === null || row.width_mm === undefined ? null : Number(row.width_mm),
      height_mm: row.height_mm === null || row.height_mm === undefined ? null : Number(row.height_mm),
      is_required: Boolean(row.is_required),
      sort_order: index + 1,
    })),
  }
}

/**
 * Reference catalogs (equipment, personnel, colors, thread codes) are only needed
 * by their pickers, so each one is loaded on first use and a failure can never
 * block the passport editor itself.
 */
async function ensureEquipmentReferences(): Promise<EquipmentReference[]> {
  if (referenceCatalogs.equipment) return equipmentReferences.value
  referenceCatalogs.equipment = true
  try {
    equipmentReferences.value = await listEquipmentReferences(client)
  } catch {
    referenceCatalogs.equipment = false
    equipmentReferences.value = []
  }
  return equipmentReferences.value
}

async function ensureApprovalPeople(): Promise<ProductionEmployee[]> {
  if (referenceCatalogs.personnel) return approvalPeople.value
  referenceCatalogs.personnel = true
  try {
    approvalPeople.value = await listPersonnelEmployees(client)
  } catch {
    referenceCatalogs.personnel = false
    approvalPeople.value = []
  }
  return approvalPeople.value
}

async function ensureColorReferences(): Promise<ColorReference[]> {
  if (referenceCatalogs.colors) return colorReferences.value
  referenceCatalogs.colors = true
  try {
    colorReferences.value = await listColorReferences(client)
  } catch {
    referenceCatalogs.colors = false
    colorReferences.value = []
  }
  return colorReferences.value
}

async function ensureThreadCodeReferences(): Promise<ThreadCodeReference[]> {
  if (referenceCatalogs.threadCodes) return threadCodeReferences.value
  referenceCatalogs.threadCodes = true
  try {
    threadCodeReferences.value = await listThreadCodes(client)
  } catch {
    referenceCatalogs.threadCodes = false
    threadCodeReferences.value = []
  }
  return threadCodeReferences.value
}

async function load(): Promise<void> {
  loading.value = true
  loadError.value = ''
  try {
    if (isEditing.value) {
      passport.value = await getTechnicalPassport(client, passportId.value)
      if (passport.value.status === 'approved') {
        loadError.value = t('engineering.passportValidation.approvedRevision')
        return
      }
      const currentPassport = passport.value
      product.value = await getProduct(client, currentPassport.product_id)
      Object.assign(form, {
        template_type: currentPassport.template_type ?? '',
        sock_type: currentPassport.sock_type ?? '',
        size_range: currentPassport.size_range ?? '',
        sock_weight_gram: Number(currentPassport.sock_weight_gram ?? 66),
        machine_type: currentPassport.machine_type ?? '',
        machine_needle_count: Number(currentPassport.machine_needle_count ?? 0),
        fixed_measurements: mergeFixedMeasurements(
          normalizeFixedMeasurements(currentPassport.fixed_measurements),
          fixedMeasurementsFromDimensions(currentPassport.dimensions),
        ),
        description: currentPassport.description ?? '',
        prepared_by: currentPassport.prepared_by ?? '',
        rubber_return_place: currentPassport.rubber_return_place ?? '',
        cotton_yarn: currentPassport.cotton_yarn ?? '',
        spandex_yarn: currentPassport.spandex_yarn ?? '',
        rubber_yarn: currentPassport.rubber_yarn ?? '',
        nylon_yarn: currentPassport.nylon_yarn ?? '',
        main_product_image: currentPassport.main_product_image ?? product.value.photo_url ?? '',
        technical_drawing_image: currentPassport.technical_drawing_image ?? '',
        technologist_id: currentPassport.technologist_id ? Number(currentPassport.technologist_id) : undefined,
        shift_manager_id: currentPassport.shift_manager_id ? Number(currentPassport.shift_manager_id) : undefined,
        manager_id: currentPassport.manager_id ? Number(currentPassport.manager_id) : undefined,
        technologist_approved_at: currentPassport.technologist_approved_at ?? '',
        shift_manager_approved_at: currentPassport.shift_manager_approved_at ?? '',
        manager_approved_at: currentPassport.manager_approved_at ?? '',
        reject_reason: currentPassport.reject_reason ?? '',
        status: currentPassport.status,
        revision_no: String((currentPassport.revisions?.length ?? 0) + 1),
        raw_materials: (currentPassport.raw_materials ?? []).map((row) => ({
          ...row,
        })),
        routes: (currentPassport.routes ?? []).map((row) => ({ ...row })),
        measurements: (currentPassport.measurements ?? []).map((row) => ({ ...row })),
        machines: (currentPassport.machines ?? []).map((row) => ({ ...row })),
        qc_items: (currentPassport.qc_items ?? []).map((row) => ({ ...row })),
        packaging: (currentPassport.packaging ?? []).map((row) => ({ ...row })),
        labels: (currentPassport.labels ?? []).map((row) => ({ ...row })),
        threads: (currentPassport.threads ?? []).map((row) => ({ ...row })),
        dimensions: { ...initialDimensions, ...(currentPassport.dimensions ?? {}) },
        elements: (currentPassport.elements?.length
          ? currentPassport.elements
          : defaultSockElements({ ...initialDimensions, ...(currentPassport.dimensions ?? {}) })
        ).map((row) => restoreDecorativeElement({ ...row })),
        yarns: (currentPassport.yarns?.length
          ? currentPassport.yarns
          : defaultSockYarns()
        ).map((row) => ({ ...row })),
        costing: (currentPassport.costing ?? []).map((row) => ({ ...row })),
        attachments: [
          ...(currentPassport.attachments ?? []).map((row) => ({ ...row })),
          ...((currentPassport.knitting_machine_design_files ?? [])
            .filter((value) => value?.trim())
            .filter((value) => !(currentPassport.attachments ?? []).some((row) => row.attachment_type === 'machine_file' && row.file_url?.trim() === value.trim()))
            .map((value, index) => ({
              attachment_type: 'machine_file',
              file_name: value.split('/').pop() || `machine-file-${index + 1}`,
              file_url: value,
              sort_order: (currentPassport.attachments?.length ?? 0) + index + 1,
            }))),
        ],
      })
      applyCurrentUserDefaults()
      pendingMainProductImageBase64.value = ''
      pendingMainProductImagePreview.value = ''
      pendingTechnicalDrawingBase64.value = ''
      pendingTechnicalDrawingPreview.value = ''
      mainProductImageStatus.value = ''
      technicalDrawingStatus.value = ''
    } else {
      if (!Number.isInteger(productId.value) || productId.value <= 0) {
        throw new Error(t('engineering.passportValidation.productId'))
      }
      product.value = await getProduct(client, productId.value)
      form.main_product_image = product.value.photo_url ?? ''
      applyCurrentUserDefaults()
      pendingMainProductImageBase64.value = ''
      pendingMainProductImagePreview.value = ''
      pendingTechnicalDrawingBase64.value = ''
      pendingTechnicalDrawingPreview.value = ''
      mainProductImageStatus.value = ''
      technicalDrawingStatus.value = ''
    }
    try {
      const versions = await listProductBomVersions(client, targetProductId.value)
      const activeVersion = versions.find((version) => Boolean(version.is_active)) ?? null
      const latestVersion = versions.reduce<BomVersionSummary | null>(
        (latest, version) => (!latest || version.version_number > latest.version_number ? version : latest),
        null,
      )
      activeBom.value = activeVersion ? await getBomVersion(client, activeVersion.id) : null
      latestBom.value = latestVersion
        ? latestVersion.id === activeVersion?.id
          ? activeBom.value
          : await getBomVersion(client, latestVersion.id)
        : null
    } catch {
      activeBom.value = null
      latestBom.value = null
    }
    // Legacy rows stored the aliased stage (`knitting`); move them onto the BOM operation codes
    // so every material row matches an option of the stage list.
    if (latestBom.value) {
      const stageByLegacyCode = new Map<string, string>()
      latestBom.value.operations.forEach((operation) => {
        stageByLegacyCode.set(legacyStageCodeForOperation(operation), stageCodeForOperation(operation))
      })
      form.raw_materials.forEach((row) => {
        const current = (row.stage_code ?? '').trim().toLowerCase()
        if (!current || current.startsWith('op_')) return
        const mapped = stageByLegacyCode.get(current)
        if (mapped) row.stage_code = mapped
      })
    }
    // Materials are shared with the BOM: routes come from the active version and the
    // passport keeps its own rows while gaining every material that only exists in the BOM.
    if (activeBom.value) {
      form.routes = applyBomToPassport(activeBom.value).routes
      form.raw_materials = mergeBomMaterialsIntoPassport(activeBom.value, form.raw_materials)
    }
    await ensureEquipmentReferences()
    initialFormState.value = deepClone(form as unknown as Record<string, unknown>)
  } catch (error: unknown) {
    loadError.value = formatApiError(error)
  } finally {
    loading.value = false
  }
}

/**
 * Raw materials are shared with the product BOM. Before saving:
 * BOM-only materials are merged into the passport, then the passport's rows are
 * written back to the operation of their stage (one new immutable BOM version,
 * only when something actually changed).
 */
async function syncMaterialsWithBom(): Promise<void> {
  const bom = activeBom.value
  if (!bom) return
  form.raw_materials = mergeBomMaterialsIntoPassport(bom, form.raw_materials)
  if (!canManageBom.value) return
  const result = applyPassportMaterialsToBom(bom, form.raw_materials)
  if (result.added === 0 && result.updated === 0) return
  activeBom.value = await updateBomVersion(client, bom.id, result.payload)
}

async function submit(): Promise<void> {
  if (!validate()) return
  saving.value = true
  submitError.value = ''
  try {
    try {
      await syncMaterialsWithBom()
    } catch (error: unknown) {
      // The BOM copy of the materials must not silently diverge from the passport.
      submitError.value = `${t('engineering.passportForm.bomSyncFailed')} ${formatApiError(error)}`
      return
    }
    const result = isEditing.value
      ? await updateTechnicalPassport(client, passportId.value, payload())
      : await createTechnicalPassport(client, payload())
    await router.push(`/technical-passports/${result.id}`)
  } catch (error: unknown) {
    submitError.value = formatApiError(error)
  } finally {
    saving.value = false
  }
}

async function submitForApproval(): Promise<void> {
  form.status = 'pending_approval'
  await submit()
  if (submitError.value || Object.keys(fieldErrors).length) form.status = 'draft'
}

async function saveDraft(): Promise<void> {
  form.status = 'draft'
  await submit()
}

function approvalTimestamp(): string {
  const now = new Date()
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
}

async function approvePassport(): Promise<void> {
  const previousStatus = form.status
  const previousApprovals = {
    technologist_approved_at: form.technologist_approved_at,
    shift_manager_approved_at: form.shift_manager_approved_at,
    manager_approved_at: form.manager_approved_at,
  }
  const stampedAt = approvalTimestamp()
  form.status = 'approved'
  form.technologist_approved_at = form.technologist_id ? (form.technologist_approved_at || stampedAt) : ''
  form.shift_manager_approved_at = form.shift_manager_id ? (form.shift_manager_approved_at || stampedAt) : ''
  form.manager_approved_at = form.manager_id ? (form.manager_approved_at || stampedAt) : ''
  await submit()
  if (submitError.value || Object.keys(fieldErrors).length) {
    form.status = previousStatus
    form.technologist_approved_at = previousApprovals.technologist_approved_at
    form.shift_manager_approved_at = previousApprovals.shift_manager_approved_at
    form.manager_approved_at = previousApprovals.manager_approved_at
  }
}

async function rejectPassport(): Promise<void> {
  const reason = form.reject_reason.trim()
  if (!reason) {
    submitError.value = t('engineering.passportForm.rejectReasonRequired')
    return
  }
  if (!window.confirm(t('engineering.passportForm.rejectConfirm'))) return
  const previousStatus = form.status
  const previousApprovals = {
    technologist_id: form.technologist_id,
    shift_manager_id: form.shift_manager_id,
    manager_id: form.manager_id,
    technologist_approved_at: form.technologist_approved_at,
    shift_manager_approved_at: form.shift_manager_approved_at,
    manager_approved_at: form.manager_approved_at,
  }
  form.status = 'rejected'
  form.technologist_id = undefined
  form.shift_manager_id = undefined
  form.manager_id = undefined
  form.technologist_approved_at = ''
  form.shift_manager_approved_at = ''
  form.manager_approved_at = ''
  form.change_description = `${form.change_description.trim() ? `${form.change_description.trim()} — ` : ''}${t('engineering.passportForm.rejectReasonLabel')}: ${reason}`
  await submit()
  if (submitError.value || Object.keys(fieldErrors).length) {
    form.status = previousStatus
    form.technologist_id = previousApprovals.technologist_id
    form.shift_manager_id = previousApprovals.shift_manager_id
    form.manager_id = previousApprovals.manager_id
    form.technologist_approved_at = previousApprovals.technologist_approved_at
    form.shift_manager_approved_at = previousApprovals.shift_manager_approved_at
    form.manager_approved_at = previousApprovals.manager_approved_at
  }
}

onMounted(load)
</script>

<template>
  <div class="page-toolbar">
    <RouterLink class="back-link" :data-tooltip="$t('engineering.passportDetail.back')" :to="backPath">{{ $t('engineering.passportDetail.back') }}</RouterLink>
    <div class="page-toolbar__actions">
      <button class="secondary-button" type="submit" form="passport-form" :disabled="loading || saving">{{ saving ? $t('engineering.passportForm.saving') : $t('engineering.passportForm.saveDraft') }}</button>
      <button type="button" :disabled="loading || saving" @click="submitForApproval">
        {{ saving && form.status === 'pending_approval' ? $t('engineering.passportForm.submitting') : $t('engineering.passportForm.submitApproval') }}
      </button>
      <button type="button" :disabled="loading || saving" @click="approvePassport">
        {{ saving && form.status === 'approved' ? $t('engineering.passportForm.approving') : $t('engineering.passportForm.approve') }}
      </button>
      <button class="reject-button" type="button" :disabled="loading || saving || form.status === 'approved'" @click="rejectPassport">
        {{ saving && form.status === 'rejected' ? $t('engineering.passportForm.rejecting') : $t('engineering.passportForm.reject') }}
      </button>
    </div>
  </div>
  <div v-if="loading" class="page-state">{{ $t('engineering.passportForm.loading') }}</div>
  <div v-else-if="loadError" class="page-state page-state--error" role="alert">
    <p>{{ loadError }}</p>
    <RouterLink :to="backPath">{{ $t('engineering.passportForm.returnProduct') }}</RouterLink>
  </div>
  <form v-else id="passport-form" class="passport-form" @submit.prevent="saveDraft">
    <header>
      <div>
        <p class="eyebrow">{{ isEditing ? $t('engineering.passportForm.passportNumber', { id: passportId }) : $t('engineering.passportForm.newPassport') }}</p>
        <h2>{{ productDisplayName }}</h2>
        <p>{{ $t('engineering.passportForm.intro') }}</p>
      </div>
    </header>

    <p v-if="submitError" class="form-error" role="alert">{{ submitError }}</p>
    <div class="passport-layout">
      <aside class="passport-summary" :aria-label="$t('engineering.passportForm.summaryTitle')">
        <div class="summary-card">
          <div class="summary-card__head">
            <div>
              <p class="summary-card__eyebrow">{{ $t('engineering.passportForm.summaryTitle') }}</p>
              <p class="summary-card__caption">{{ productDisplayName }}</p>
            </div>
            <span class="summary-status" :data-status="form.status">{{ $t(`engineering.passport.status.${form.status}`) }}</span>
          </div>
          <div class="summary-card__content">
            <dl class="summary-facts">
              <div><dt>{{ $t('engineering.passportForm.sockType') }}</dt><dd>{{ sockTypeLabel || '—' }}</dd></div>
              <div><dt>{{ $t('engineering.passportForm.season') }}</dt><dd>{{ product?.season || '—' }}</dd></div>
              <div><dt>{{ $t('engineering.passportForm.sizeRange') }}</dt><dd>{{ form.size_range || '—' }}</dd></div>
              <div><dt>{{ $t('engineering.passportForm.sockWeight') }}</dt><dd>{{ form.sock_weight_gram || '—' }}</dd></div>
              <div><dt>{{ $t('engineering.passportForm.needleCount') }}</dt><dd>{{ form.machine_needle_count || '—' }}</dd></div>
              <div><dt>{{ $t('engineering.passportForm.machines') }}</dt><dd>{{ form.machines.length }}</dd></div>
            </dl>
            <div class="summary-health">
              <div v-if="passport?.completeness" class="completeness">
                <p :class="passport.completeness.is_approvable ? 'summary-ok' : 'summary-alert'">
                  {{ passport.completeness.is_approvable ? $t('engineering.passportForm.approvable') : $t('engineering.passportForm.notApprovable') }}
                </p>
                <p v-if="missingSectionNames.length" class="summary-changes">
                  {{ $t('engineering.passportForm.missingSections') }}: {{ missingSectionNames.join(', ') }}
                </p>
                <p v-if="passport.completeness.missing_fields?.length" class="summary-changes">
                  {{ $t('engineering.passportForm.missingFields') }}: {{ passport.completeness.missing_fields.join(', ') }}
                </p>
              </div>
              <p v-if="fieldErrorCount" class="summary-alert">{{ $t('engineering.passportForm.summaryErrors', { count: fieldErrorCount }) }}</p>
              <p v-else class="summary-ok">{{ $t('engineering.passportForm.summaryReady') }}</p>
              <p v-if="changedSectionCount" class="summary-changes">{{ $t('engineering.passportForm.summaryChanges', { count: changedSectionCount }) }}</p>
            </div>
          </div>
          <nav class="summary-nav" :aria-label="$t('engineering.passportForm.sectionsAria')">
            <a
              v-for="tab in tabs"
              :key="tab.id"
              class="summary-nav__item"
              :class="{ invalid: tabHasErrors(tab) }"
              :href="`#passport-panel-${tab.id}`"
              @click.prevent="scrollToSection(tab.id)"
            >
              <span class="summary-nav__label">{{ tab.label }}</span>
              <span v-if="tabErrorCount(tab)" class="summary-nav__badge">{{ tabErrorCount(tab) }}</span>
              <span v-else-if="tabHasChanges(tab)" class="summary-nav__dot" aria-hidden="true">*</span>
            </a>
          </nav>
        </div>
      </aside>
      <div class="passport-sections">
    <section id="passport-panel-general">
      <h3>{{ $t('engineering.passportForm.definition') }}</h3>
      <div class="form-grid form-grid--specs">
        <div class="wide">
          <SockTypeImageGrid
            v-model="form.sock_type"
            name="passport-sock-type"
            :title="$t('engineering.passportForm.sockType')"
            :options="sockTypeImageOptions"
            required
            :error="fieldErrors.sock_type"
          />
        </div>
        <label>{{ $t('engineering.passportForm.sizeRange') }}<input v-model="form.size_range" type="text" placeholder="36-40"></label>
        <label>{{ $t('engineering.passportForm.sockWeight') }} *
          <input v-model.number="form.sock_weight_gram" type="number" min="0.01" step="0.01">
          <small v-if="fieldErrors.sock_weight_gram">{{ fieldErrors.sock_weight_gram }}</small>
        </label>
        <label>{{ $t('engineering.passportDetail.machineType') }}
          <select v-model="form.machine_type" data-testid="passport-machine-type">
            <option value="">{{ $t('engineering.equipment.machineTypeUnset') }}</option>
            <option v-for="machineType in machineTypeOptions" :key="machineType" :value="machineType">
              {{ machineTypeLabel(machineType) }}
            </option>
          </select>
        </label>
        <label>{{ $t('engineering.passportForm.needleCount') }} *
          <input v-model.number="form.machine_needle_count" type="number" min="1">
          <small v-if="fieldErrors.machine_needle_count">{{ fieldErrors.machine_needle_count }}</small>
        </label>
        <label class="wide">{{ $t('engineering.passportForm.description') }}<textarea v-model="form.description" rows="3"></textarea></label>
      </div>
    </section>

    <div id="passport-panel-structure" class="structure-panel">
      <SockSmartDimensions :fixed-measurements="form.fixed_measurements" :sock-type="form.sock_type" />
      <section class="sizes-card">
        <div class="section-heading">
          <div><h3>{{ $t('engineering.passportForm.sizes') }}</h3><p>{{ $t('engineering.passportForm.measurementsHelp') }}</p></div>
        </div>
        <div v-if="!form.measurements.length" class="empty-row">{{ $t('engineering.passportForm.noMeasurements') }}</div>
        <div v-else class="sheet-scroll">
          <table class="sheet-table sheet-table--sizes">
            <thead>
              <tr>
                <th class="col-index">#</th>
                <th class="col-stage">{{ $t('engineering.passportForm.code') }}</th>
                <th class="col-material">{{ $t('engineering.passportForm.description') }}</th>
                <th class="col-thread">{{ $t('engineering.passportForm.value') }}</th>
                <th class="col-unit">{{ $t('engineering.passportForm.unit') }}</th>
                <th class="col-qty">{{ $t('engineering.passportForm.rows') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in form.measurements" :key="index">
                <td class="col-index">{{ index + 1 }}</td>
                <td class="col-stage"><span class="cell-text">{{ row.code || '—' }}</span></td>
                <td class="col-material"><span class="cell-text">{{ row.description || '—' }}</span></td>
                <td class="col-thread"><span class="cell-text">{{ row.measurement_value || '—' }}</span></td>
                <td class="col-unit"><span class="cell-text">{{ row.measurement_unit || '—' }}</span></td>
                <td class="col-qty"><span class="cell-text cell-number">{{ row.row_count ?? '—' }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
      <p v-if="Object.keys(fieldErrors).some((key) => key.startsWith('fixed_measurement_'))" class="form-error" role="alert">
        {{ $t('engineering.passportForm.structureError') }}
      </p>
    </div>

    <section id="passport-panel-materials">
      <div class="section-heading">
        <div><h3>{{ $t('engineering.passportForm.rawMaterials') }}</h3><p>{{ $t('engineering.passportForm.materialsHelp') }}</p></div>
        <button class="secondary-button" type="button" @click="addMaterial">{{ $t('engineering.passportForm.addMaterial') }}</button>
      </div>
      <div v-if="!form.raw_materials.length" class="empty-row">{{ $t('engineering.passportForm.noMaterials') }}</div>
      <div v-else class="sheet-scroll">
        <table class="sheet-table sheet-table--materials">
          <thead>
            <tr>
              <th class="col-index">#</th>
              <th class="col-stage">{{ $t('engineering.passportForm.stage') }} *</th>
              <th class="col-material">{{ $t('engineering.passportForm.selectRawMaterial') }} *</th>
              <th class="col-qty">{{ $t('engineering.passportForm.consumptionPair') }} *</th>
              <th class="col-qty">{{ $t('engineering.passportForm.waste') }}</th>
              <th class="col-unit">{{ $t('engineering.passportStructure.unit') }}</th>
              <th class="col-color">{{ $t('engineering.passportForm.colorName') }}</th>
              <th class="col-thread">{{ $t('engineering.passportForm.threadCode') }}</th>
              <th class="col-check">{{ $t('engineering.passportForm.required') }}</th>
              <th class="col-action"><span class="visually-hidden">{{ $t('engineering.passportForm.remove') }}</span></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(row, index) in form.raw_materials" :key="index">
              <tr :class="{ 'row-invalid': Boolean(fieldErrors[`material_${index}`]) }">
                <td class="col-index">{{ index + 1 }}</td>
                <td class="col-stage">
                  <select v-model="row.stage_code" class="cell-input">
                    <option v-if="row.stage_code && !bomStageOptions.some((option) => option.value === row.stage_code)" :value="row.stage_code">{{ row.stage_code }}</option>
                    <option v-for="option in bomStageOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                  </select>
                </td>
                <td class="col-material">
                  <button class="cell-picker" type="button" @click="openProductPicker('material', index)">
                    <span :class="{ placeholder: !row.material_name_snapshot }">{{ row.material_name_snapshot || $t('engineering.passportForm.selectRawMaterial') }}</span>
                    <small>{{ row.material_product_id ? `#${row.material_product_id}` : $t('engineering.passportForm.browseProducts') }}</small>
                  </button>
                </td>
                <td class="col-qty"><input v-model.number="row.consumption_per_pair" class="cell-input cell-number" min="0" step="0.0001" type="number"></td>
                <td class="col-qty"><input v-model.number="row.waste_percent" class="cell-input cell-number" min="0" step="0.1" type="number"></td>
                <td class="col-unit"><input v-model="row.uom" class="cell-input" type="text"></td>
                <td class="col-color">
                  <button class="cell-picker" type="button" :title="row.color_name || undefined" @click="openThreadColorPicker(index, 'material')">
                    <span :class="{ placeholder: !row.color_name }">{{ row.color_name || $t('engineering.yarnCatalog.selectColor') }}</span>
                    <small>{{ row.thread_code || '—' }}</small>
                  </button>
                </td>
                <td class="col-thread">
                  <button class="cell-picker" type="button" :title="row.thread_code || undefined" @click="openThreadCodePicker(index, 'material')">
                    <span :class="{ placeholder: !row.thread_code }">{{ row.thread_code || $t('engineering.passportForm.threadCode') }}</span>
                    <small>{{ $t('engineering.passportForm.browseProducts') }}</small>
                  </button>
                </td>
                <td class="col-check"><input v-model="row.is_required_for_start" type="checkbox"></td>
                <td class="col-action">
                  <button class="row-remove" type="button" :aria-label="$t('engineering.passportForm.remove')" :title="$t('engineering.passportForm.remove')" @click="removeMaterial(index)">×</button>
                </td>
              </tr>
              <tr v-if="fieldErrors[`material_${index}`]" class="row-error">
                <td />
                <td colspan="9"><small>{{ fieldErrors[`material_${index}`] }}</small></td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </section>

    <section id="passport-panel-threads">
      <div class="section-heading">
        <div><h3>{{ $t('engineering.passportForm.threads') }}</h3><p>{{ $t('engineering.passportForm.threadsHelp') }}</p></div>
        <button class="secondary-button" type="button" @click="addThread">{{ $t('engineering.passportForm.addThread') }}</button>
      </div>
      <div v-if="!form.threads.length" class="empty-row">{{ $t('engineering.passportForm.noThreads') }}</div>
      <div v-else class="sheet-scroll">
        <table class="sheet-table sheet-table--threads">
          <thead>
            <tr>
              <th class="col-index">#</th>
              <th class="col-color">{{ $t('engineering.passportForm.colorName') }}</th>
              <th class="col-name">{{ $t('engineering.passportForm.colorName') }} *</th>
              <th class="col-thread">{{ $t('engineering.passportForm.threadCode') }}</th>
              <th class="col-thread">{{ $t('engineering.passportForm.threadCode') }} *</th>
              <th class="col-action"><span class="visually-hidden">{{ $t('engineering.passportForm.remove') }}</span></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(row, index) in form.threads" :key="index">
              <tr :class="{ 'row-invalid': Boolean(fieldErrors[`thread_${index}`]) }">
                <td class="col-index">{{ index + 1 }}</td>
                <td class="col-color">
                  <button class="cell-picker" type="button" @click="openThreadColorPicker(index)">
                    <span :class="{ placeholder: !row.thread_color_name }">{{ row.thread_color_name || $t('engineering.yarnCatalog.selectColor') }}</span>
                    <small>{{ row.thread_code || $t('engineering.passportForm.browseProducts') }}</small>
                  </button>
                </td>
                <td class="col-name"><input v-model="row.thread_color_name" class="cell-input" type="text" readonly></td>
                <td class="col-thread">
                  <button class="cell-picker" type="button" @click="openThreadCodePicker(index)">
                    <span :class="{ placeholder: !row.thread_code }">{{ row.thread_code || $t('engineering.passportForm.threadCode') }}</span>
                    <small>{{ row.thread_code_master?.name || $t('engineering.passportForm.browseProducts') }}</small>
                  </button>
                </td>
                <td class="col-thread"><input v-model="row.thread_code" class="cell-input" type="text" readonly></td>
                <td class="col-action">
                  <button class="row-remove" type="button" :aria-label="$t('engineering.passportForm.remove')" :title="$t('engineering.passportForm.remove')" @click="removeThread(index)">×</button>
                </td>
              </tr>
              <tr v-if="fieldErrors[`thread_${index}`]" class="row-error">
                <td />
                <td colspan="5"><small>{{ fieldErrors[`thread_${index}`] }}</small></td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </section>

    <section id="passport-panel-machines">
      <div class="section-heading">
        <div><h3>{{ $t('engineering.passportForm.compatibleMachines') }}</h3><p>{{ $t('engineering.passportForm.machinesHelp') }}</p></div>
        <div class="machine-actions">
          <button class="secondary-button" type="button" @click="openMachinePicker()">{{ $t('engineering.passportForm.selectMachine') }}</button>
        </div>
      </div>
      <div v-if="!form.machines.length" class="empty-row">{{ $t('engineering.passportForm.noMachines') }}</div>
      <div v-else class="sheet-scroll">
        <table class="sheet-table sheet-table--machines">
          <thead>
            <tr>
              <th class="col-index">#</th>
              <th class="col-material">{{ $t('engineering.passportForm.machine') }} *</th>
              <th class="col-qty">{{ $t('engineering.passportForm.needleCount') }} *</th>
              <th class="col-check">{{ $t('engineering.passportForm.compatible') }}</th>
              <th class="col-action"><span class="visually-hidden">{{ $t('engineering.passportForm.remove') }}</span></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(row, index) in form.machines" :key="index">
              <tr :class="{ 'row-invalid': Boolean(fieldErrors[`machine_${index}`]) }">
                <td class="col-index">{{ index + 1 }}</td>
                <td class="col-material">
                  <button class="cell-picker" type="button" @click="openMachinePicker(index)">
                    <span :class="{ placeholder: !row.machine_name }">{{ row.machine_name || $t('engineering.passportForm.selectMachine') }}</span>
                    <small>{{ row.equipment_reference_id ? `#${row.equipment_reference_id}` : $t('engineering.passportForm.machineName') }}</small>
                  </button>
                </td>
                <td class="col-qty"><input v-model.number="row.needle_count" class="cell-input cell-number" min="1" type="number"></td>
                <td class="col-check"><input v-model="row.is_compatible" type="checkbox"></td>
                <td class="col-action">
                  <button class="row-remove" type="button" :aria-label="$t('engineering.passportForm.remove')" :title="$t('engineering.passportForm.remove')" @click="removeMachine(index)">×</button>
                </td>
              </tr>
              <tr v-if="fieldErrors[`machine_${index}`]" class="row-error">
                <td />
                <td colspan="4"><small>{{ fieldErrors[`machine_${index}`] }}</small></td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </section>

    <section id="passport-panel-packaging">
      <div class="section-heading">
        <div><h3>{{ $t('engineering.passportForm.packaging') }}</h3><p>{{ $t('engineering.passportForm.packagingHelp') }}</p></div>
        <button class="secondary-button" type="button" @click="addPackaging">{{ $t('engineering.passportForm.addPackaging') }}</button>
      </div>
      <div v-if="!form.packaging.length" class="empty-row">{{ $t('engineering.passportForm.noPackaging') }}</div>
      <div v-else class="sheet-scroll">
        <table class="sheet-table">
          <thead>
            <tr>
              <th class="col-index">#</th>
              <th class="col-product">{{ $t('engineering.passportForm.packagingProduct') }}</th>
              <th class="col-ref">{{ $t('engineering.passportForm.referenceId') }}</th>
              <th class="col-name">{{ $t('engineering.passportForm.packagingName') }}</th>
              <th class="col-qty">{{ $t('engineering.passportForm.quantity') }} *</th>
              <th class="col-action"><span class="visually-hidden">{{ $t('engineering.passportForm.remove') }}</span></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(row, index) in form.packaging" :key="index">
              <tr :class="{ 'row-invalid': Boolean(fieldErrors[`packaging_${index}`]) }">
                <td class="col-index">{{ index + 1 }}</td>
                <td class="col-product">
                  <button class="cell-picker" type="button" @click="openProductPicker('packaging', index)">
                    <span :class="{ placeholder: !row.packaging_name }">{{ row.packaging_name || $t('engineering.passportForm.selectPackaging') }}</span>
                    <small>{{ row.packaging_product_id ? `#${row.packaging_product_id}` : $t('engineering.passportForm.browseProducts') }}</small>
                  </button>
                </td>
                <td class="col-ref"><input v-model.number="row.packaging_reference_id" class="cell-input" min="1" type="number"></td>
                <td class="col-name"><input v-model="row.packaging_name" class="cell-input" type="text"></td>
                <td class="col-qty"><input v-model.number="row.quantity" class="cell-input cell-number" min="0.000001" step="0.000001" type="number"></td>
                <td class="col-action">
                  <button class="row-remove" type="button" :aria-label="$t('engineering.passportForm.remove')" :title="$t('engineering.passportForm.remove')" @click="removePackaging(index)">×</button>
                </td>
              </tr>
              <tr v-if="fieldErrors[`packaging_${index}`]" class="row-error">
                <td />
                <td colspan="5"><small>{{ fieldErrors[`packaging_${index}`] }}</small></td>
              </tr>
            </template>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="4">{{ $t('engineering.passportForm.totalQuantity') }}</td>
              <td class="col-qty cell-total">{{ packagingTotal.toFixed(2) }}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </section>

    <section id="passport-panel-labels">
      <div class="section-heading">
        <div><h3>{{ $t('engineering.passportForm.labels') }}</h3><p>{{ $t('engineering.passportForm.labelsHelp') }}</p></div>
        <button class="secondary-button" type="button" @click="addLabel">{{ $t('engineering.passportForm.addLabel') }}</button>
      </div>
      <div v-if="!form.labels.length" class="empty-row">{{ $t('engineering.passportForm.noLabels') }}</div>
      <div v-else class="sheet-scroll">
        <table class="sheet-table sheet-table--labels">
          <thead>
            <tr>
              <th class="col-index">#</th>
              <th class="col-material">{{ $t('engineering.passportForm.labelProduct') }}</th>
              <th class="col-name">{{ $t('engineering.passportForm.labelName') }} *</th>
              <th class="col-thread">{{ $t('engineering.passportForm.labelCode') }}</th>
              <th class="col-check">{{ $t('engineering.passportForm.required') }}</th>
              <th class="col-action"><span class="visually-hidden">{{ $t('engineering.passportForm.remove') }}</span></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(row, index) in form.labels" :key="index">
              <tr :class="{ 'row-invalid': Boolean(fieldErrors[`label_${index}`]) }">
                <td class="col-index">{{ index + 1 }}</td>
                <td class="col-material">
                  <button class="cell-picker" type="button" @click="openProductPicker('label', index)">
                    <span :class="{ placeholder: !row.label_name }">{{ row.label_name || $t('engineering.passportForm.selectLabel') }}</span>
                    <small>{{ row.label_code || $t('engineering.passportForm.browseProducts') }}</small>
                  </button>
                </td>
                <td class="col-name"><input v-model="row.label_name" class="cell-input" type="text"></td>
                <td class="col-thread"><input v-model="row.label_code" class="cell-input" type="text"></td>
                <td class="col-check"><input v-model="row.is_required" type="checkbox"></td>
                <td class="col-action">
                  <button class="row-remove" type="button" :aria-label="$t('engineering.passportForm.remove')" :title="$t('engineering.passportForm.remove')" @click="removeLabel(index)">×</button>
                </td>
              </tr>
              <tr v-if="fieldErrors[`label_${index}`]" class="row-error">
                <td />
                <td colspan="5"><small>{{ fieldErrors[`label_${index}`] }}</small></td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </section>

    <section id="passport-panel-attachments">
      <div class="section-heading">
        <div><h3>{{ $t('engineering.passportForm.attachments') }}</h3><p>{{ $t('engineering.passportForm.attachmentsHelp') }}</p></div>
      </div>
      <div class="spec-section">
        <div class="form-grid form-grid--uploads">
          <div>
            <FileUploadField
              :label="$t('engineering.passportForm.mainProductImage')"
              :model-value="form.main_product_image"
              path-readonly
              accept=".png,.jpg,.jpeg,.webp,.gif"
              :button-label="$t('engineering.passportForm.chooseFile')"
              :helper-text="$t('engineering.passportForm.imageUploadHelp')"
              :status-text="mainProductImageStatus"
              :error-text="submitError === $t('engineering.passportForm.imageUploadTypeError') ? submitError : ''"
              @update:model-value="form.main_product_image = $event"
              @selected="applyMainProductImageFile"
            />
            <small v-if="fieldErrors.legacy_main_product_image">{{ fieldErrors.legacy_main_product_image }}</small>
          </div>
          <div>
            <FileUploadField
              :label="$t('engineering.passportForm.technicalDrawingImage')"
              :model-value="form.technical_drawing_image"
              path-readonly
              accept=".png,.jpg,.jpeg,.webp,.gif"
              :button-label="$t('engineering.passportForm.chooseFile')"
              :helper-text="$t('engineering.passportForm.imageUploadHelp')"
              :status-text="technicalDrawingStatus"
              :error-text="submitError === $t('engineering.passportForm.imageUploadTypeError') ? submitError : ''"
              @update:model-value="form.technical_drawing_image = $event"
              @selected="applyTechnicalDrawingFile"
            />
            <small v-if="fieldErrors.legacy_technical_drawing_image">{{ fieldErrors.legacy_technical_drawing_image }}</small>
          </div>
          <div>
            <FileUploadField
              :label="$t('engineering.passportForm.knittingProgramFile')"
              :model-value="knittingProgramFile()"
              path-readonly
              :button-label="$t('engineering.passportForm.chooseFile')"
              :helper-text="$t('engineering.passportForm.attachmentUploadHelp')"
              :status-text="knittingProgramStatus"
              :error-text="submitError === $t('engineering.passportForm.imageUploadTypeError') ? submitError : ''"
              @update:model-value="setKnittingProgramFile"
              @selected="applyKnittingProgramFile"
            />
            <small v-if="fieldErrors.legacy_knitting_machine_design_files">{{ fieldErrors.legacy_knitting_machine_design_files }}</small>
          </div>
        </div>
      </div>
    </section>

    <section class="approval-panel" aria-labelledby="passport-approval-heading">
      <div class="section-heading section-heading--compact">
        <div>
          <h3 id="passport-approval-heading">{{ $t('engineering.passportForm.approval') }}</h3>
          <p>{{ $t('engineering.passportForm.approvalHelp') }}</p>
        </div>
      </div>
      <div class="approval-personnel-grid">
        <label>
          <span>{{ $t('engineering.passportDetail.technologist') }}</span>
          <button class="product-select" type="button" @click="openPersonnelPicker('technologist')">
            <span>{{ selectedPersonnelName(form.technologist_id) || $t('engineering.passportForm.selectApprover') }}</span>
            <small>{{ form.technologist_id ? `#${form.technologist_id}` : $t('engineering.passportForm.browsePersonnel') }}</small>
          </button>
        </label>
        <label>
          <span>{{ $t('engineering.passportDetail.shiftManager') }}</span>
          <button class="product-select" type="button" @click="openPersonnelPicker('shift_manager')">
            <span>{{ selectedPersonnelName(form.shift_manager_id) || $t('engineering.passportForm.selectApprover') }}</span>
            <small>{{ form.shift_manager_id ? `#${form.shift_manager_id}` : $t('engineering.passportForm.browsePersonnel') }}</small>
          </button>
        </label>
        <label>
          <span>{{ $t('engineering.passportDetail.manager') }}</span>
          <button class="product-select" type="button" @click="openPersonnelPicker('manager')">
            <span>{{ selectedPersonnelName(form.manager_id) || $t('engineering.passportForm.selectApprover') }}</span>
            <small>{{ form.manager_id ? `#${form.manager_id}` : $t('engineering.passportForm.browsePersonnel') }}</small>
          </button>
        </label>
      </div>
    </section>

      </div>
    </div>
    <ProductPickerModal
      :open="productPicker.open"
      :product-type="'raw_material'"
      :title="productPicker.target === 'packaging'
        ? $t('engineering.passportForm.selectPackaging')
        : productPicker.target === 'label'
          ? $t('engineering.passportForm.selectLabel')
          : $t('engineering.passportForm.selectRawMaterial')"
      @close="productPicker.open = false"
      @select="selectProduct"
    />
    <PersonnelPickerModal
      :open="personnelPicker.open"
      :title="$t('engineering.passportForm.selectApprover')"
      @close="personnelPicker.open = false"
      @select="selectApprovalPerson"
    />
    <ColorPickerModal
      :open="threadColorPicker.open"
      :title="$t('engineering.yarnCatalog.selectColor')"
      :colors="colorReferences"
      @close="threadColorPicker.open = false"
      @select="selectThreadColor"
    />
    <ThreadCodePickerModal
      :open="threadCodePicker.open"
      :title="$t('engineering.passportForm.threadCode')"
      :items="threadCodeReferences"
      @close="threadCodePicker.open = false"
      @created="addThreadCodeReference"
      @select="selectThreadCode"
    />
    <EquipmentPickerModal
      :open="machinePicker.open"
      :title="$t('engineering.passportForm.selectMachine')"
      :equipment="equipmentReferences"
      @close="machinePicker.open = false"
      @select="selectMachine"
    />
  </form>
</template>

<style scoped>
.back-link { display: inline-block; margin-bottom: 20px; color: #285b9a; font-weight: 700; }
.page-state { display: grid; min-height: 240px; place-items: center; }
.page-state--error, .form-error, label small { color: #852d22; }
.passport-form { display: grid; gap: 16px; }
.structure-panel { display: grid; gap: 14px; }
.sizes-card { padding: 18px; border: 1px solid #d7dfdc; background: #fff; }
.completeness { display: grid; gap: 4px; margin-top: 4px; }
.passport-layout { display: grid; grid-template-columns: minmax(0, 1fr); gap: 16px; }
.passport-sections { display: grid; gap: 16px; min-width: 0; }
.passport-sections > section, .passport-sections > .structure-panel { scroll-margin-top: 12px; }
.passport-summary { min-width: 0; }
.summary-card { display: grid; gap: 10px; padding: 14px 16px; border: 1px solid #cfd9d5; border-left: 4px solid #285b9a; background: linear-gradient(135deg, #fff 0%, #fbfdff 100%); box-shadow: 0 2px 8px rgb(30 55 80 / 6%); }
.summary-card__head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.summary-card__eyebrow { margin: 0; color: #66726e; font-size: 11px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; }
.summary-card__caption { margin: 2px 0 0; color: #173454; font-size: 14px; font-weight: 750; }
.summary-status { flex: 0 0 auto; padding: 4px 9px; border: 1px solid #cfd8d5; border-radius: 999px; color: #53625d; background: #f3f6f5; font-size: 10px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; }
.summary-status[data-status="approved"] { border-color: #9fd3c2; color: #0b6b4f; background: #e8f6f0; }
.summary-status[data-status="pending_approval"] { border-color: #e0c493; color: #8a4b0f; background: #fdf3e4; }
.summary-status[data-status="rejected"] { border-color: #d7a8a1; color: #852d22; background: #fdf1ef; }
.summary-card__content { display: grid; grid-template-columns: minmax(0, 3fr) minmax(220px, 1fr); gap: 12px; align-items: stretch; }
.summary-facts { display: grid; grid-template-columns: repeat(6, minmax(90px, 1fr)); margin: 0; border: 1px solid #e2e9e6; border-radius: 6px; background: #fff; }
.summary-facts > div { display: grid; gap: 2px; min-width: 0; padding: 8px 10px; border-right: 1px solid #e8eeeb; }
.summary-facts > div:last-child { border-right: 0; }
.summary-facts dt { overflow: hidden; color: #74807c; font-size: 10px; font-weight: 700; letter-spacing: .02em; text-overflow: ellipsis; text-transform: uppercase; white-space: nowrap; }
.summary-facts dd { overflow: hidden; margin: 0; color: #173454; font-size: 13px; font-weight: 800; text-overflow: ellipsis; white-space: nowrap; }
.summary-health { display: flex; min-width: 0; flex-direction: column; justify-content: center; gap: 4px; padding: 7px 10px; border: 1px solid #e2e9e6; border-radius: 6px; background: #f7faf9; }
.summary-alert, .summary-ok, .summary-changes { margin: 0; font-size: 12px; font-weight: 700; }
.summary-alert { color: #852d22; }
.summary-ok { color: #0b6b4f; }
.summary-changes { color: #285b9a; }
.summary-nav { display: flex; flex-wrap: wrap; gap: 4px; padding-top: 8px; border-top: 1px solid #e5ebe8; }
.summary-nav__item { display: inline-flex; min-height: 26px; flex: 1 1 auto; align-items: center; justify-content: center; gap: 6px; padding: 4px 8px; border: 1px solid #e0e7e4; border-radius: 5px; color: #3d4c47; background: #fff; font-size: 11px; font-weight: 650; text-align: center; text-decoration: none; }
.summary-nav__item:hover { border-color: #aac2dc; color: #17457d; background: #f2f7fc; }
.summary-nav__item.invalid { color: #852d22; }
.summary-nav__badge { display: inline-grid; min-width: 17px; height: 17px; padding: 0 4px; border-radius: 9px; color: #fff; background: #b44b3d; font-size: 10px; font-weight: 800; place-items: center; }
.summary-nav__dot { color: #07996f; font-weight: 800; }
@media (max-width: 1050px) {
  .summary-card__content { grid-template-columns: 1fr; }
  .summary-facts { grid-template-columns: repeat(3, minmax(100px, 1fr)); }
  .summary-facts > div:nth-child(3) { border-right: 0; }
  .summary-facts > div:nth-child(-n + 3) { border-bottom: 1px solid #e8eeeb; }
}
@media (max-width: 640px) {
  .summary-card { padding: 12px; }
  .summary-facts { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .summary-facts > div:nth-child(3) { border-right: 1px solid #e8eeeb; }
  .summary-facts > div:nth-child(2n) { border-right: 0; }
  .summary-facts > div:nth-child(-n + 4) { border-bottom: 1px solid #e8eeeb; }
  .summary-nav__item { flex-basis: calc(50% - 4px); }
}
.backend-help-card { gap: 10px; background: #fbfcfb; }
.backend-help-card--current { border-color: #c87b2b; background: #fff8ef; }
.backend-help-card__head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.backend-help-card__definition { margin: 0; color: #31433e; }
.backend-help-card__list { display: grid; gap: 6px; margin: 0; padding-left: 18px; color: #31433e; }
.backend-help-badge { display: inline-flex; align-items: center; min-height: 24px; padding: 0 10px; border-radius: 999px; color: #8a4b0f; background: #f8ddbe; font-size: 12px; font-weight: 700; }
header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.header-actions { display: flex; gap: 10px; }
.eyebrow { margin: 0 0 2px; color: #66726e; font-size: 11px; font-weight: 700; text-transform: uppercase; }
h2 { margin: 0; font-size: 22px; line-height: 1.2; } header p:last-child { margin: 3px 0 0; color: #66726e; font-size: 13px; }
section { padding: 22px; border: 1px solid #d7dfdc; background: #fff; }
h3 { margin: 0 0 18px; }
.section-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.section-heading--compact { margin-bottom: 12px; }
.section-heading h3 { margin-bottom: 4px; }
.section-heading p { margin: 0; color: #66726e; font-size: 13px; }
.spec-section { display: grid; gap: 12px; margin-top:20px; }
.approval-panel { padding: 18px; border: 1px solid #cfd9d5; border-left: 4px solid #3568a8; background: #f8faf9; }
.approval-personnel-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; margin-top: 14px; }
.approval-personnel-grid .product-select { width: 100%; min-width: 0; }
.approval-personnel-grid .product-select span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.approval-diagnostics { display: grid; gap: 10px; margin-top: 16px; padding: 18px; border: 1px solid #e5c7c2; background: #fff7f5; }
.approval-diagnostics__head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.approval-diagnostics__head strong { color: #852d22; }
.approval-diagnostics__head span { color: #66726e; font-size: 12px; font-weight: 700; text-transform: uppercase; }
.approval-diagnostics__message { margin: 0; color: #852d22; line-height: 1.5; }
.approval-diagnostics__list { display: grid; gap: 6px; margin: 0; padding-left: 18px; color: #53625d; }
.attachment-diagnostics { margin-top: 0; }
.form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.form-grid--uploads { align-items: start; }
.form-grid--uploads > div { min-width: 0; }
.form-grid--specs { grid-template-columns: repeat(4, minmax(0, 1fr)); }
label { display: grid; gap: 6px; color: #53625d; font-size: 13px; font-weight: 700; }
.wide { grid-column: 1 / -1; }
input, select, textarea { width: 100%; box-sizing: border-box; padding: 9px 10px; border: 1px solid #b9c5c1; font: inherit; }
button { padding: 10px 16px; border: 0; color: #fff; background: #3568a8; font-weight: 700; }
button:disabled { opacity: .6; }
.reject-button { color: #fff; background: #b0301e; }
.reject-button:hover:not(:disabled) { background: #8f2517; }
.secondary-button { border: 1px solid #b9c5c1; color: #285b9a; background: #fff; }
.secondary-button--inline { align-self: end; }
.product-select { display: flex; align-items: center; justify-content: space-between; min-height: 40px; padding: 8px 10px; border: 1px solid #aebdcc; color: #173454; background: #fff; text-align: left; }
.product-select small { color: #66726e; font-weight: 500; }
.repeatable-list { display: grid; gap: 10px; margin-top: 18px; }
.repeatable-list--compact { margin-top: 10px; }
.repeatable-row { position: relative; display: grid; gap: 12px; padding: 16px; border: 1px solid #d7dfdc; background: #f8faf9; }
.repeatable-row--compact { grid-template-columns: minmax(0, 1fr) auto; padding: 12px; }
.qc-row { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.label-row, .thread-row, .attachment-row { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.sequence { display: grid; width: 30px; height: 30px; color: #fff; background: #3568a8; font-weight: 700; place-items: center; }
.check-label { display: flex; align-items: center; align-self: end; min-height: 38px; }
.check-label input { width: auto; }
.attachment-link { align-self: end; color: #285b9a; font-size: 13px; font-weight: 700; text-decoration: none; }
.remove-button { align-self: end; border: 1px solid #d7a8a1; color: #852d22; background: #fff; }
.row-error { grid-column: 1 / -1; color: #852d22; }
.empty-row { margin-top: 16px; padding: 14px; color: #66726e; background: #f3f6f5; }
.visually-hidden { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
.sheet-scroll { margin-top: 16px; overflow-x: auto; border: 1px solid #cfd8d5; }
.sheet-table { width: 100%; min-width: 720px; border-collapse: collapse; font-size: 13px; }
.sheet-table th, .sheet-table td { padding: 0; border: 1px solid #dde4e1; text-align: left; vertical-align: middle; }
.sheet-table thead th { padding: 8px 10px; color: #3d4c47; background: #eef2f0; font-size: 11px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; white-space: nowrap; }
.sheet-table tbody tr:nth-child(even) { background: #fbfcfc; }
.sheet-table tbody tr:hover { background: #f4f8f6; }
.sheet-table tfoot td { padding: 8px 10px; color: #3d4c47; background: #eef2f0; font-size: 12px; font-weight: 700; text-align: right; }
.sheet-table .col-index { width: 44px; padding: 6px 10px; color: #66726e; text-align: center; font-variant-numeric: tabular-nums; }
.sheet-table thead .col-index { color: #3d4c47; }
.sheet-table .col-ref { width: 110px; }
.sheet-table .col-qty { width: 130px; }
.sheet-table .col-action { width: 44px; text-align: center; }
.sheet-table .col-qty.cell-total { font-variant-numeric: tabular-nums; }
.cell-input { width: 100%; box-sizing: border-box; padding: 9px 10px; border: 0 !important; background: transparent; font: inherit; }
.cell-input:focus { outline: 2px solid #3568a8; outline-offset: -2px; background: #fff; }
.cell-number { text-align: right; font-variant-numeric: tabular-nums; }
.cell-picker { display: flex; align-items: center; justify-content: space-between; gap: 10px; width: 100%; padding: 8px 10px; border: 0 !important; color: #173454; background: transparent; text-align: left; font-weight: 600; }
.cell-picker:hover:not(:disabled) { background: #eef4fa; }
/* Picker labels stay on one line: long names are truncated with an ellipsis. */
.cell-picker > span { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cell-picker small { flex: 0 1 auto; min-width: 0; overflow: hidden; color: #66726e; font-size: 11px; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.cell-picker .placeholder { color: #8a9792; font-weight: 500; }
.sheet-table .row-invalid td { background: #fdf4f2; }
.sheet-table tr.row-error td { padding: 6px 10px; border-color: #e7cdc7; color: #852d22; background: #fdf4f2; }
.sheet-table tr.row-error small { font-weight: 600; }
.row-remove { width: 30px; height: 30px; padding: 0; border: 0 !important; color: #852d22; background: transparent; font-size: 18px; font-weight: 700; line-height: 1; }
.row-remove:hover:not(:disabled) { background: #f6e3df; }
.machine-actions { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.sheet-table .col-check { width: 92px; text-align: center; }
.sheet-table .col-check input[type="checkbox"] { display: block; width: 16px; height: 16px; margin: 0 auto; padding: 0; border: 0; }
.sheet-table--materials { min-width: 1040px; }
.sheet-table--threads { min-width: 820px; }
.sheet-table--labels { min-width: 720px; }
.sheet-table--sizes { min-width: 720px; }
.sheet-table--machines { min-width: 560px; }
.sheet-table .col-stage { width: 120px; }
.sheet-table .col-material { min-width: 220px; }
.sheet-table .col-name { min-width: 160px; }
.sheet-table .col-unit { width: 80px; }
.sheet-table .col-color { width: 130px; }
.sheet-table .col-thread { width: 130px; }
.cell-text { display: block; padding: 9px 10px; color: #173454; font-weight: 600; }
.cell-sub { color: #8a9792; font-size: 11px; font-weight: 600; }
.sheet-table tr.row-error td { border-color: #e7cdc7; }
@media (max-width: 680px) { .sheet-scroll { margin-right: -16px; margin-left: -16px; border-right: 0; border-left: 0; } }
.linked-fieldset { min-width: 0; margin: 0; padding: 0; border: 0; }
.linked-fieldset:disabled { opacity: .82; }
.bom-link-notice { display: grid; gap: 4px; margin-top: 16px; padding: 12px 14px; border: 1px solid #9db9d8; color: #173454; background: #f2f7fc; font-size: 13px; }
.bom-link-notice span { color: #53625d; }
.secondary-link { padding: 10px 16px; border: 1px solid #b9c5c1; color: #285b9a; background: #fff; font-weight: 700; text-decoration: none; }
@media (max-width: 900px) { .qc-row, .label-row, .thread-row, .attachment-row { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 1100px) { .form-grid--specs { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 680px) { header, .section-heading { align-items: stretch; flex-direction: column; } .header-actions { flex-direction: column; } .form-grid, .form-grid--specs, .approval-personnel-grid, .qc-row, .label-row, .thread-row, .attachment-row { grid-template-columns: 1fr; } .wide { grid-column: auto; } }
</style>
